import { useEffect, useState, useMemo } from "react";
import { reminderAPI, api } from "services";
import { formatMoney, numSort, stringSort } from "utils";
import dayjs from "dayjs";
import { ShimmerLoader } from "components";
import ExpandableSearch from "./SearchFilter";

const Collection = () => {
    const [overdueAccounts, setOverdueAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        fetchOverdueAccounts();
    }, []);

    const fetchOverdueAccounts = async () => {
        setLoading(true);
        try {
            const response = await reminderAPI.getOverdueAccounts();
            setOverdueAccounts(response);
        } catch (error) {
            console.error("Error fetching overdue accounts:", error);
        } finally {
            setLoading(false);
        }
    }

    const filteredAccounts = useMemo(() => {
        return overdueAccounts.filter(account => {
            const name = account.get("name")?.toLowerCase() || "";
            const phone = account.get("phone") || "";
            const type = account.get("tag") || "";
            const term = searchTerm.toLowerCase();
            return name.includes(term) || phone.includes(term) || type.includes(term);
        });
    }, [overdueAccounts, searchTerm]);

    const downloadPdfReport = async () => {
        await api.downloadCollectionReport();
    }

    const downloadExcelReport = () => {
        console.log("downloadExcelReport");
    }

    return (
        <div className="collection-report" >
            {/* Header Section */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                <div>
                    <h3 className="fw-bold text-dark mb-1">Collection Report</h3>
                    <p className="text-muted small mb-0">List of accounts with overdue payments</p>
                </div>
                <div className="d-flex  gap-2">
                    <ExpandableSearch value={searchTerm}
                        onChange={setSearchTerm} />
                    <button className="btn btn-outline-success d-flex align-items-center gap-2 px-3 py-2 rounded-3" disabled onClick={downloadExcelReport} title="Download Excel">
                        <i className="bi bi-file-earmark-excel"></i>
                        <span className="d-none d-lg-inline">Excel</span>
                    </button>
                    <button className="btn btn-outline-danger d-flex align-items-center gap-2 px-3 py-2 rounded-3" disabled onClick={downloadPdfReport} title="Download PDF">
                        <i className="bi bi-file-earmark-pdf"></i>
                        <span className="d-none d-lg-inline">PDF</span>
                    </button>
                </div>
            </div>

            {/* Table Section */}
            <div className="border rounded-3 shadow-sm overflow-hidden">
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="bg-light border-bottom sticky-top">
                            <tr>
                                <th className="p-1 p-md-3 text-muted fs-xmd-smaller fw-bold text-uppercase">#</th>
                                <th className="p-1 p-md-3 text-muted fs-xmd-smaller fw-bold text-uppercase">Account Name</th>
                                <th className="p-1 p-md-3 text-muted fs-xmd-smaller fw-bold text-uppercase">Contact</th>
                                <th className="p-1 p-md-3 text-muted fs-xmd-smaller fw-bold text-uppercase">Overdue Amount</th>
                                <th className="p-1 p-md-3 text-muted fs-xmd-smaller fw-bold text-uppercase">Overdue Period</th>
                                <th className="p-1 p-md-3 text-muted fs-xmd-smaller fw-bold text-uppercase">Type</th>
                                <th className="p-1 p-md-3 text-muted fs-xmd-smaller fw-bold text-uppercase">Address</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                [...Array(5)].map((_, i) => (
                                    <tr key={i}>
                                        <td colSpan="7" className="p-0 border-0">
                                            <div className="py-2 px-4">
                                                <div className="shimmer-wrapper w-100 mb-2" style={{ height: '30px', borderRadius: '4px' }}></div>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : filteredAccounts.length > 0 ? (
                                filteredAccounts.map((overdueAccount, index) => (
                                    <tr key={overdueAccount.id}>
                                        <td className="p-2 px-md-3 text-muted">{index + 1}</td>
                                        <td className="p-2 px-md-3">
                                            <div className="fw-bold text-dark">{overdueAccount.get("name")}</div>
                                        </td>
                                        <td className="p-2 px-md-3">
                                            <div className="d-flex align-items-center gap-2">
                                                <small>{overdueAccount.get("phone") == "+91" || !overdueAccount.get("phone") ? "N/A" : overdueAccount.get("phone")}</small>
                                            </div>
                                        </td>
                                        <td className="p-2 px-md-3">
                                            <span className="fw-bold text-danger">
                                                {formatMoney(overdueAccount.get("balance"))}
                                            </span>
                                        </td>
                                        <td className="p-2 px-md-3">
                                            <span className="badge bg-light text-dark border rounded-pill px-3 py-2 fw-normal">
                                                {dayjs(overdueAccount.get("reminder")?.date).fromNow()}
                                            </span>
                                        </td>
                                        <td className="p-2 px-md-3">
                                            <span className="badge border text-app border-app rounded-pill px-3 py-1 fw-medium">
                                                {overdueAccount.get("tag")}
                                            </span>
                                        </td>
                                        <td className="p-2 px-md-3">
                                            <div className="text-wrap small text-muted" style={{ maxWidth: '200px' }}>
                                                {overdueAccount.get("address") || "N/A"}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center py-5">
                                        <div className="py-4">
                                            <i className="bi bi-search fs-1 text-muted mb-3 d-block"></i>
                                            <h5 className="text-dark fw-bold">No accounts found</h5>
                                            <p className="text-muted">Try adjusting your search or filters</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default Collection;